import React from 'react';
import { useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import SEO from '../components/SEO';
import SiteContainer from '../components/site/SiteContainer';
import Eyebrow from '../components/site/Eyebrow';
import Heading from '../components/site/Heading';
import Button from '../components/site/Button';
import { SITE_HEADER_OFFSET, useSiteChrome } from '../layouts/PageStates';

/**
 * Catch-all for unknown paths and flag-off surfaces (audit row 110), in the Sept 2026
 * redesign's look in every flag state: it renders under the site chrome (App.js's
 * public catch-all: clears the fixed header) and bare inside portal routes
 * (routes/public, business, venue, admin: no header offset). Paper grid, "404 / Not on
 * the table" eyebrow, one Faustina h1, two ways on, the tabletop illustration (ERR-404-01). Not indexed: <SEO noIndex> with the
 * title "Page not found" (SEO appends " | Tappd", like every other page) and the requested
 * path as its canonical (never the home page).
 */
/** The tabletop illustration (public/images/site): since U4 it appears only on this page. */
const TABLETOP = {
  src: '/images/site/tabletop-hero-800.webp',
  srcSet: '/images/site/tabletop-hero-800.webp 800w, /images/site/tabletop-hero-1400.webp 1400w, /images/site/tabletop-hero-1672.webp 1672w',
};

const NotFound = () => {
  const inChrome = useSiteChrome();
  const { pathname } = useLocation();
  return (
    <main
      className={cn(
        'site-paper site-light flex min-h-[70vh] items-center pb-section text-navy',
        inChrome ? SITE_HEADER_OFFSET : 'pt-section',
      )}
      data-testid="not-found"
    >
      <SEO
        title="Page not found"
        description="The page you were looking for is not on Tappd. Start again from the home page or browse local deals."
        url={pathname}
        noIndex
      />
      <SiteContainer className="grid items-center gap-12 nav:grid-cols-[1.12fr_0.88fr] nav:gap-10">
        <div className="min-w-0">
          <Eyebrow index="404" tone="rust">Not on the table</Eyebrow>
          <Heading level={1} accent="rust" className="mb-[1.625rem] mt-5">
            That page isn&rsquo;t <em>here.</em>
          </Heading>
          <p className="max-w-[36.875rem] text-[1.0625rem] leading-[1.65] text-brand-muted nav:text-[1.1875rem]">
            The link may be old, or the page may have moved. Start again from the home page,
            or see which local businesses are on the table near you.
          </p>
          <div className="mt-[1.875rem] flex flex-col items-stretch gap-3.5 sm:flex-row sm:flex-wrap sm:items-center">
            <Button to="/">Back to home</Button>
            <Button to="/local-deals-near-me" variant="secondary">Find deals near me</Button>
          </div>
        </div>
        {/* ERR-404-01 (upgrade brief §3.14): the tabletop illustration, retired from every other
            page, with the lost coaster on its corner. Decorative only. */}
        <div aria-hidden="true" className="relative min-w-0 pb-10 pr-6 nav:pb-14 nav:pr-10">
          <img
            src={TABLETOP.src}
            srcSet={TABLETOP.srcSet}
            sizes="(min-width: 800px) 44vw, 92vw"
            alt=""
            width="1672"
            height="941"
            decoding="async"
            className="block h-auto w-full border border-brand-rule shadow-[6px_8px_0] shadow-navy/10"
          />
          <div className="absolute bottom-0 right-0 grid size-[8.5rem] -rotate-[8deg] place-items-center rounded-full bg-navy text-brand-sheet shadow-[6px_8px_0] shadow-navy/10 nav:size-[11rem]">
            <div className="grid size-[7rem] place-items-center rounded-full border-2 border-dashed border-brand-peach/60 text-center nav:size-[9.25rem]">
              <div>
                <span className="block font-display text-[2.75rem] font-medium leading-none tracking-[-0.03em] nav:text-[3.5rem]">404</span>
                <span className="mt-1.5 block font-label text-[10px] uppercase tracking-[0.07em] text-brand-peach nav:text-xs">Lost coaster</span>
              </div>
            </div>
          </div>
        </div>
      </SiteContainer>
    </main>
  );
};

export default NotFound;
